import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DndContext, PointerSensor, TouchSensor, closestCenter,
  useDroppable, useSensor, useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { api } from "../lib/api";
import { ymd } from "../lib/date";
import { addDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useAuth, useEffectiveUser } from "../lib/auth";
import { can, canSeeBeta } from "../lib/permissions";
import { FixCoverageModal } from "../components/FixCoverageModal";
import { NotificationsWidget } from "../components/NotificationsWidget";
import { SortableWidget } from "../components/SortableWidget";
import {
  useHiddenWidgetCategories, widgetCategoryFromId,
} from "../lib/dashboardWidgets";
import {
  RequestCoverageButton, ReviewCoverageRequestModal,
} from "../components/CoverageRequestModals";
import {
  eligibleCoverageTypes, coverageRequestTypeLabel, homeShiftDateForOT,
} from "../lib/coverage";
import { applyTacOverlay, isShiftActiveOn } from "../lib/tacMode";
import { readableTextOn } from "../lib/colors";
import { hourOf, parseHourNote } from "../lib/hourBuckets";
import { TIME_OFF_CODE, TIME_OFF_LABEL } from "../lib/types";
import type { ShiftCode } from "../lib/types";
import type {
  Agency, Assignment, CoverageRequest, Department, Employee, Form,
  ShiftCoverage, Shift, TimeOff, User,
} from "../lib/types";


export function Dashboard() {
  const me = useEffectiveUser();
  const previewLevel = useAuth((s) => s.previewLevel);
  const inPreview = previewLevel !== null;
  const mayFix = can.editRoster(me);
  // Per-user widget visibility (Settings → Dashboard widgets). A
  // category hidden here drops every widget whose id maps to that
  // category — "overstaffed" hides both the all-dept consolidated
  // widget and every per-dept one at once.
  const [hiddenWidgetCategories] = useHiddenWidgetCategories();
  const today = ymd(new Date());
  const twoWeeksOut = ymd(addDays(new Date(), 14));
  const [fixCoverage, setFixCoverage] = useState<{ shiftId: string; date: string } | null>(null);
  // Coverage window is fixed at 30 days for both Understaffed and
  // Overstaffed. The old 7d / 2w / month toggle was more UI than
  // supervisors used — locking to "month" keeps the widgets simple
  // and always shows the full upcoming month in one glance.
  const rangeEnd = useMemo(() => ymd(addDays(new Date(), 30)), []);
  const pendingRangeEnd = useMemo(() => ymd(addDays(new Date(), 90)), []);

  // Owner and L1 see every department's KPI + widget row stacked on
  // the dashboard — one section per dept, ordered by dept sortOrder.
  // L2 / L3 / L4 see a single dept via the dropdown (default is the
  // signed-in user's home dept, persisted in localStorage).
  const showAllDepts = me?.level === "owner" || me?.level === "l1";
  const isSupervisor =
    me?.level === "owner" || me?.level === "l1" || me?.level === "l2";

  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const allEmployees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  // Agency-wide shifts + coverage so we can render per-dept sections
  // without spawning N queries. DeptSection filters to its own dept.
  const allShifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const shiftCodes = useQuery({ queryKey: ["shiftCodes"], queryFn: () => api.listShiftCodes() });
  // Agency-wide 14-day assignments + time-off, filtered client-side
  // per employee (the signed-in user for the primary widget, plus
  // any forwarded users' employees). Not gated on me?.employeeId
  // because an owner with no linked employee still needs the data
  // to render forwarded employees' schedule widgets.
  const myAssignments = useQuery({
    queryKey: ["assignments", today, twoWeeksOut],
    queryFn: () => api.listAssignments(today, twoWeeksOut),
  });
  const myTimeOff = useQuery({
    queryKey: ["timeoff", today, twoWeeksOut],
    queryFn: () => api.listTimeOff(today, twoWeeksOut),
  });
  // Notification-forwarding subscriptions. Each row identifies a
  // from_user whose notifications this account also receives. Used
  // here to render an extra "Schedule for <Name>" widget per
  // forwarded user (helpful for the owner-testing-employee flow).
  const forwards = useQuery({
    queryKey: ["myForwards"],
    queryFn: () => api.listMyForwards(),
    enabled: !!me,
  });
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    enabled: !!me,
  });
  const allCoverage = useQuery({
    queryKey: ["coverage", today, rangeEnd],
    queryFn: () => api.coverage(today, rangeEnd),
  });
  const windowAssignments = useQuery({
    queryKey: ["assignments", today, rangeEnd],
    queryFn: () => api.listAssignments(today, rangeEnd),
  });
  const pendingRequests = useQuery({
    queryKey: ["dashboard-pending-requests", today, pendingRangeEnd],
    queryFn: () => api.listTimeOff(today, pendingRangeEnd),
    enabled: !!me,
  });
  // Approved time-off in the coverage window. Powers the Request-
  // Coverage button's eligibility check — an officer with approved
  // time-off on the shortage date is filtered out.
  const windowTimeOff = useQuery({
    queryKey: ["timeoff", today, rangeEnd],
    queryFn: () => api.listTimeOff(today, rangeEnd),
    enabled: !!me,
  });
  // Pending coverage requests across the agency. Used by both the
  // requester's own dashboard (to badge "already requested") and
  // the supervisor's Coverage-requests widget.
  const coverageRequests = useQuery({
    queryKey: ["coverageRequests"],
    queryFn: () => api.listCoverageRequests({ status: "pending" }),
    enabled: !!me,
  });
  // Every form the agency has defined (built-ins + custom). The
  // pending-forms widget below uses this to resolve a submission's
  // formSlug back to a human-readable form name.
  const formsQuery = useQuery({
    queryKey: ["forms"],
    queryFn: () => api.listForms(),
  });
  const disabledFeatures = useMemo(
    () => new Set(agency.data?.disabledFeatures ?? []),
    [agency.data?.disabledFeatures],
  );

  // Drag-to-reorder for the dashboard widgets. Two activation
  // paths so the same DOM works on desktop and mobile without
  // fighting normal clicks / taps / scrolls:
  //   * PointerSensor with distance 8 — desktop press-and-drag
  //     starts once the pointer moves 8 px; a plain click doesn't
  //     kick it in, so buttons / links inside the widget still work.
  //   * TouchSensor with delay 1500 ms + 8 px tolerance — mobile
  //     needs a stationary 1.5 s long-press to start a drag. Any
  //     finger movement over 8 px inside the delay cancels the
  //     activation, so a normal swipe just scrolls the page
  //     without picking up a widget by accident. A quick tap
  //     fires normally.
  // The widget's outer div stays touch-auto until a drag actually
  // starts (see SortableWidget.tsx), so a resting finger never
  // blocks iOS scroll either.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 1500, tolerance: 8 },
    }),
  );
  // Four fixed drop columns. `columnAssignments` stores which widget
  // ids live in each column and their vertical order within it.
  // Widgets that appear later (a fresh forwarded schedule, a newly-
  // created dept) get auto-appended to the shortest column so they
  // stay visible without shoving the user's arrangement around. The
  // whole layout persists per-device.
  const NUM_COLUMNS = 4;
  const [columnAssignments, setColumnAssignments] = useState<string[][]>(() => {
    try {
      const raw = localStorage.getItem("dashboard.widgetColumns");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every(Array.isArray)) {
          const arr = parsed as string[][];
          while (arr.length < NUM_COLUMNS) arr.push([]);
          return arr.slice(0, NUM_COLUMNS);
        }
      }
    } catch { /* ignore */ }
    return Array.from({ length: NUM_COLUMNS }, () => []);
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "dashboard.widgetColumns",
        JSON.stringify(columnAssignments),
      );
    } catch { /* ignore */ }
  }, [columnAssignments]);

  // Per-widget collapse state. Two independent overrides so the
  // effective isCollapsed can flip either way from the default
  // (empty widgets default collapsed; non-empty default expanded):
  //   userCollapsedIds — non-empty widgets the user manually closed
  //   userExpandedIds — empty widgets the user manually opened
  const [userCollapsedIds, setUserCollapsedIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("dashboard.userCollapsed");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return new Set(parsed as string[]);
      }
    } catch { /* ignore */ }
    return new Set();
  });
  const [userExpandedIds, setUserExpandedIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("dashboard.userExpanded");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return new Set(parsed as string[]);
      }
    } catch { /* ignore */ }
    return new Set();
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "dashboard.userCollapsed",
        JSON.stringify([...userCollapsedIds]),
      );
    } catch { /* ignore */ }
  }, [userCollapsedIds]);
  useEffect(() => {
    try {
      localStorage.setItem(
        "dashboard.userExpanded",
        JSON.stringify([...userExpandedIds]),
      );
    } catch { /* ignore */ }
  }, [userExpandedIds]);

  // Server-side persistence of the same dashboard state. On mount
  // we fetch the saved row and — if present — replace the local
  // state with it so the arrangement follows the user across
  // devices and reinstalls. Local storage still provides the
  // instant first paint. `serverHydratedRef` gates the save
  // effects so we don't ship the pre-hydration local state back to
  // the server the moment the page mounts (which would overwrite a
  // newer server row with a stale local one).
  const serverPrefs = useQuery({
    queryKey: ["dashboardPrefs", me?.id],
    queryFn: () => api.getDashboardPrefs(),
    enabled: !!me,
    staleTime: Infinity,
  });
  const serverHydratedRef = useRef(false);
  useEffect(() => {
    if (serverHydratedRef.current) return;
    if (!me) return;
    if (serverPrefs.isFetching) return;
    // First arrival — apply server state if any exists, then mark
    // hydrated so subsequent local changes get saved back.
    const p = serverPrefs.data;
    if (p) {
      if (p.columns.length > 0) {
        const arr = p.columns.slice();
        while (arr.length < NUM_COLUMNS) arr.push([]);
        setColumnAssignments(arr.slice(0, NUM_COLUMNS));
      }
      setUserCollapsedIds(new Set(p.userCollapsed));
      setUserExpandedIds(new Set(p.userExpanded));
    }
    serverHydratedRef.current = true;
  }, [me, serverPrefs.data, serverPrefs.isFetching]);

  // Debounced push of the current layout + collapse state back to
  // the server. 800 ms feels responsive without hammering Supabase
  // on every drag frame. Skips until after the initial server
  // hydration completes (see serverHydratedRef above). Also
  // skipped while a supervisor is previewing another level so a
  // "view as L2" session doesn't overwrite the real user's saved
  // owner layout with the preview's dept-scoped widget set.
  const saveTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!serverHydratedRef.current) return;
    if (!me) return;
    if (inPreview) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      api.saveDashboardPrefs({
        columns: columnAssignments,
        userCollapsed: [...userCollapsedIds],
        userExpanded: [...userExpandedIds],
      }).catch(() => { /* swallow — local state still valid */ });
    }, 800);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [me, inPreview, columnAssignments, userCollapsedIds, userExpandedIds]);

  // Exiting preview mode: rehydrate local state from the server
  // row so the user drops back into their real layout even if the
  // preview mutated columnAssignments along the way. We reset the
  // hydration ref and refetch; the initial-hydration effect above
  // fires again as if fresh.
  const prevPreviewLevelRef = useRef(previewLevel);
  useEffect(() => {
    if (prevPreviewLevelRef.current === previewLevel) return;
    prevPreviewLevelRef.current = previewLevel;
    if (previewLevel === null) {
      serverHydratedRef.current = false;
      serverPrefs.refetch();
    }
  }, [previewLevel, serverPrefs]);

  // Dept resolution — unused for L1/Owner (they see all).
  const myEmpDeptId = useMemo(
    () => (allEmployees.data ?? []).find((e) => e.id === me?.employeeId)?.departmentId ?? null,
    [allEmployees.data, me?.employeeId],
  );
  // L2 / L3 / L4 view is locked to the signed-in user's home
  // department — no dropdown to override, no localStorage. Falls
  // through to the first available dept if the officer isn't linked
  // to one (e.g. a fresh owner login before an employee record is
  // attached). Owner / L1 don't consume resolvedDeptId at all; they
  // render one section per dept via showAllDepts.
  const resolvedDeptId = useMemo(() => {
    const list = departments.data ?? [];
    if (list.length === 0) return null;
    if (myEmpDeptId && list.some((d) => d.id === myEmpDeptId)) return myEmpDeptId;
    return list[0].id;
  }, [departments.data, myEmpDeptId]);

  // Agency-wide employee lookup for the pending panels — a Dispatch
  // officer's pending request needs a name on the Patrol section too.
  const empByIdAll = useMemo(
    () => new Map((allEmployees.data ?? []).map((e) => [e.id, e])),
    [allEmployees.data],
  );

  // Request-coverage support — resolves the current officer's
  // employee record + their in-window assignments and time-off so
  // the per-row eligibility check on the understaffed widget can
  // run without extra queries. The renderRequest render-prop below
  // uses these to decide whether to show the Request button and
  // which coverage types to offer.
  const myEmp = useMemo(
    () => (allEmployees.data ?? []).find((e) => e.id === me?.employeeId) ?? null,
    [allEmployees.data, me?.employeeId],
  );
  const myAssignmentsInWindow = useMemo(
    () => (windowAssignments.data ?? []).filter((a) => a.employeeId === me?.employeeId),
    [windowAssignments.data, me?.employeeId],
  );
  const myApprovedTimeOffInWindow = useMemo(
    () => (windowTimeOff.data ?? []).filter(
      (t) => t.employeeId === me?.employeeId && t.status === "approved",
    ),
    [windowTimeOff.data, me?.employeeId],
  );
  const myPendingCoverageRequests = useMemo(
    () => (coverageRequests.data ?? []).filter(
      (r) => r.requesterUserId === me?.id,
    ),
    [coverageRequests.data, me?.id],
  );
  // Only non-supervisors see the Request button — supervisors have
  // the Fix Coverage flow already, and the row click opens that.
  // Also gated to beta testers while the coverage-request feature
  // is still in beta (developer-controlled via Settings → Beta
  // testers). Non-beta users never see the button and never see
  // the Coverage-requests widget below.
  const betaCoverageRequests = canSeeBeta(me);
  const canRequestCoverage = !mayFix && !!myEmp && betaCoverageRequests;
  const renderRequest = useMemo(() => {
    if (!canRequestCoverage) return undefined;
    return (shiftId: string, deptId: string, date: string) => {
      // Skip if I've already submitted a pending request for this
      // (shift, date). Show a passive "Requested" chip instead so
      // the row still tells me my ask is in flight.
      const already = myPendingCoverageRequests.some(
        (r) => r.shiftId === shiftId && r.date === date,
      );
      if (already) {
        return (
          <span
            className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 whitespace-nowrap"
            title="You've submitted a request for this shift"
          >
            Requested
          </span>
        );
      }
      const types = eligibleCoverageTypes({
        employee: myEmp!,
        shortShiftId: shiftId,
        date,
        allShifts: allShifts.data ?? [],
        myAssignmentsOnDate: myAssignmentsInWindow.filter((a) => a.date === date),
        myApprovedTimeOffOnDate: myApprovedTimeOffInWindow.filter(
          (t) => t.startDate <= date && date <= t.endDate,
        ),
      });
      if (types.length === 0) return null;
      return (
        <RequestCoverageButton
          shiftId={shiftId}
          deptId={deptId}
          date={date}
          eligibleTypes={types}
        />
      );
    };
  }, [
    canRequestCoverage, myEmp, allShifts.data, myAssignmentsInWindow,
    myApprovedTimeOffInWindow, myPendingCoverageRequests,
  ]);

  // Supervisor-side pending coverage requests review modal. Opened
  // when a supervisor clicks a row on the Coverage-requests widget.
  const [reviewing, setReviewing] = useState<CoverageRequest | null>(null);

  // "My schedule" — one row per calendar day for the next 14 days
  // showing what the signed-in officer is doing that day: time
  // range, hours, or a Day off / Time off / OT label. Not dept-
  // scoped; whatever the officer has coming up regardless of which
  // dept they belong to.
  // One schedule widget per relevant employee: the signed-in user
  // if they have their own employee record, plus one per forwarded
  // user with an employee id. Owners who forward from their own
  // employee account see just Bond's widget; officers who forward
  // from a peer see two.
  const scheduleWidgets = useMemo(() => {
    const shiftById = new Map((allShifts.data ?? []).map((s) => [s.id, s]));
    const codeMap = new Map(
      (shiftCodes.data ?? []).map((c) => [c.code.toUpperCase(), c]),
    );
    const empById = new Map((allEmployees.data ?? []).map((e) => [e.id, e]));
    const usersById = new Map((users.data ?? []).map((u) => [u.id, u]));

    function buildDays(employeeId: string): MyScheduleDay[] {
      const emp0 = empById.get(employeeId);
      const homeShift = emp0?.homeShiftId
        ? (allShifts.data ?? []).find((s) => s.id === emp0.homeShiftId) ?? null
        : null;
      // Remap OT rows' calendar dates the same way the Schedule
      // page does — a split OT first-half covering a wrap-midnight
      // home chip belongs on the day BEFORE the stored date; a
      // split OT second-half of a wrap-midnight gap chip belongs
      // on the day AFTER. Without this the dashboard shows a 2–14
      // OT a day earlier than the live Schedule grid does.
      const myAss = (myAssignments.data ?? [])
        .filter((a) => a.employeeId === employeeId)
        .map((a) => {
          const role = (a.overrideReason ?? "").toLowerCase();
          const isFirstHalf = role.includes("first half");
          const isSecondHalf = role.includes("second half");
          if (!isFirstHalf && !isSecondHalf) return a;
          const otShift = (allShifts.data ?? []).find((s) => s.id === a.shiftId) ?? null;
          if (!homeShift || !otShift) return a;
          const remapped = homeShiftDateForOT(homeShift, otShift, a.date, allShifts.data ?? []);
          if (remapped && remapped !== a.date) {
            return { ...a, date: remapped };
          }
          return a;
        });
      const myTo = (myTimeOff.data ?? []).filter(
        (t) => t.employeeId === employeeId && t.status === "approved",
      );
      // Fallback shift for days with no explicit assignment — the
      // employee's home shift. If tac mode is active for the agency
      // and the officer follows tac, prefer their tac-home shift.
      const emp = empById.get(employeeId);
      let fallbackShift: Shift | null = null;
      if (emp) {
        const tacOn = agency.data?.tacModeActive === true;
        const useTac = tacOn && emp.followsTacMode && !!emp.tacHomeShiftId;
        const id = useTac ? emp.tacHomeShiftId : emp.homeShiftId;
        fallbackShift = id ? shiftById.get(id) ?? null : null;
      }
      const out: MyScheduleDay[] = [];
      for (let i = 0; i < 14; i++) {
        const date = ymd(addDays(new Date(), i));
        out.push(buildMyScheduleDay(date, myAss, myTo, shiftById, codeMap, fallbackShift));
      }
      return out;
    }

    const widgets: Array<{ key: string; title: string; days: MyScheduleDay[] }> = [];
    if (me?.employeeId) {
      widgets.push({
        key: `self:${me.employeeId}`,
        title: "My schedule — next 2 weeks",
        days: buildDays(me.employeeId),
      });
    }
    for (const f of forwards.data ?? []) {
      const u = usersById.get(f.fromUserId);
      const empId = u?.employeeId;
      if (!empId) continue;
      // Skip if this forwarded employee is already me (defensive —
      // shouldn't happen since we can't forward from ourselves).
      if (empId === me?.employeeId) continue;
      const emp = empById.get(empId);
      const name = emp ? `${emp.firstName} ${emp.lastName}`.trim() : "Forwarded";
      widgets.push({
        key: `fwd:${empId}`,
        title: `${name} — next 2 weeks`,
        days: buildDays(empId),
      });
    }
    return widgets;
  }, [
    me?.employeeId, forwards.data, users.data, allEmployees.data,
    myAssignments.data, myTimeOff.data, allShifts.data, shiftCodes.data,
    agency.data?.tacModeActive,
  ]);



  // Which departments to render sections for. Owner / L1 see all,
  // ordered by sortOrder then name. Everyone else sees just their
  // resolved dept (the dropdown selection).
  const deptsToShow: Department[] = useMemo(() => {
    const list = departments.data ?? [];
    if (showAllDepts) {
      return [...list].sort((a, b) =>
        (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
        || a.name.localeCompare(b.name));
    }
    const active = list.find((d) => d.id === resolvedDeptId);
    return active ? [active] : [];
  }, [departments.data, showAllDepts, resolvedDeptId]);

  // Compute the per-dept coverage data once per (dept, deps) — one
  // Map<deptId, DeptComputed> that the flat widget pool below reads
  // from when building the sortable widget list.
  const deptDataByDeptId = useMemo(() => {
    const map = new Map<string, DeptComputed>();
    for (const dept of deptsToShow) {
      map.set(dept.id, computeDeptCoverage({
        dept,
        departments: departments.data ?? [],
        allEmployees: allEmployees.data ?? [],
        allShifts: allShifts.data ?? [],
        allCoverage: allCoverage.data ?? [],
        allWindowAssignments: windowAssignments.data ?? [],
        allPending: pendingRequests.data ?? [],
        allForms: formsQuery.data ?? [],
        disabledFeatures,
        agency: agency.data ?? null,
        me,
        isSupervisor,
      }));
    }
    return map;
  }, [
    deptsToShow, departments.data, allEmployees.data, allShifts.data,
    allCoverage.data, windowAssignments.data, pendingRequests.data,
    formsQuery.data, disabledFeatures, agency.data, me, isSupervisor,
  ]);

  // Build the flat widget definitions — schedule widgets first,
  // then the coverage widgets. Owner / L1 (showAllDepts) get ONE
  // consolidated widget per coverage type with per-dept sections
  // labelled inside; L2 / L3 / L4 (single home dept) get the same
  // four per-dept widgets they always had.
  //
  // Each definition carries a stable id (used as the SortableContext
  // key), pre-baked JSX for the expanded state, a compact label +
  // dept badge shown when collapsed, and an `empty` flag that
  // decides the default collapse state (empty → collapsed by
  // default; non-empty → expanded).
  const allFlatWidgets = useMemo(() => {
    const defs: Array<{
      id: string;
      jsx: React.ReactNode;
      empty: boolean;
      label: string;
      deptColor: { border: string; dot: string } | null;
      deptName: string | null;
    }> = [];
    for (const w of scheduleWidgets) {
      defs.push({
        id: w.key,
        jsx: <MyScheduleWidget title={w.title} days={w.days} />,
        empty: false,
        label: w.title,
        deptColor: null,
        deptName: null,
      });
    }
    // Personal-notifications feed. One widget for every signed-in
    // user (not scoped by dept) — the inbox itself is per-account.
    // Treated as never-empty so it defaults expanded; the widget's
    // own body renders the "All caught up" state when there's no
    // unread activity.
    defs.push({
      id: "notifications",
      jsx: <NotificationsWidget />,
      empty: false,
      label: "Notifications",
      deptColor: null,
      deptName: null,
    });
    if (showAllDepts) {
      // Consolidated coverage widgets: one card per widget type,
      // with a dept-labelled section inside for each dept that has
      // something to show. Depts with no matching rows still get a
      // "—" line so supervisors can see the check happened.
      const entries = deptsToShow.map((dept) => ({
        dept,
        data: deptDataByDeptId.get(dept.id) ?? null,
        deptColor: colorForDept(dept, departments.data ?? []),
      })).filter((e) => e.data !== null) as Array<{
        dept: Department;
        data: DeptComputed;
        deptColor: { border: string; dot: string };
      }>;
      defs.push({
        id: "all:understaffed",
        jsx: <AllUnderstaffedWidget entries={entries} mayFix={mayFix} setFixCoverage={setFixCoverage} renderRequest={renderRequest} />,
        empty: entries.every((e) => e.data.understaffed.length === 0),
        label: "Understaffed shifts", deptColor: null, deptName: "All departments",
      });
      defs.push({
        id: "all:overstaffed",
        jsx: <AllOverstaffedWidget entries={entries} />,
        empty: entries.every((e) => e.data.overstaffed.length === 0),
        label: "Overstaffed shifts", deptColor: null, deptName: "All departments",
      });
      defs.push({
        id: "all:pendingTimeOff",
        jsx: <AllPendingTimeOffWidget entries={entries} empByIdAll={empByIdAll} />,
        empty: entries.every((e) => e.data.pendingTimeOffList.length === 0),
        label: "Pending time off", deptColor: null, deptName: "All departments",
      });
      defs.push({
        id: "all:pendingTraining",
        jsx: <AllPendingTrainingWidget entries={entries} empByIdAll={empByIdAll} />,
        empty: entries.every((e) => e.data.pendingTrainingList.length === 0),
        label: "Pending training", deptColor: null, deptName: "All departments",
      });
      if (isSupervisor && betaCoverageRequests) {
        // Consolidated Coverage-requests widget for owner / L1 —
        // one card listing every pending coverage request across
        // every dept. Empty when nothing's pending. Beta-gated
        // while the feature is still in test.
        const requests = coverageRequests.data ?? [];
        defs.push({
          id: "all:coverageRequests",
          jsx: (
            <CoverageRequestsWidget
              requests={requests}
              empByIdAll={empByIdAll}
              shiftsById={new Map((allShifts.data ?? []).map((s) => [s.id, s]))}
              deptsById={new Map((departments.data ?? []).map((d) => [d.id, d]))}
              onOpen={(r) => setReviewing(r)}
            />
          ),
          empty: requests.length === 0,
          label: "Coverage requests", deptColor: null, deptName: "All departments",
        });
      }
    } else {
      for (const dept of deptsToShow) {
        const data = deptDataByDeptId.get(dept.id);
        if (!data) continue;
        const dc = colorForDept(dept, departments.data ?? []);
        defs.push({
          id: `${dept.id}:understaffed`,
          jsx: <UnderstaffedWidget dept={dept} data={data} deptColor={dc} mayFix={mayFix} setFixCoverage={setFixCoverage} renderRequest={renderRequest} />,
          empty: data.understaffed.length === 0,
          label: "Understaffed shifts",
          deptColor: dc, deptName: dept.name,
        });
        defs.push({
          id: `${dept.id}:overstaffed`,
          jsx: <OverstaffedWidget dept={dept} data={data} deptColor={dc} />,
          empty: data.overstaffed.length === 0,
          label: "Overstaffed shifts",
          deptColor: dc, deptName: dept.name,
        });
        defs.push({
          id: `${dept.id}:pendingTimeOff`,
          jsx: <PendingTimeOffWidget dept={dept} data={data} deptColor={dc} isSupervisor={isSupervisor} empByIdAll={empByIdAll} />,
          empty: data.pendingTimeOffList.length === 0,
          label: "Pending time off",
          deptColor: dc, deptName: dept.name,
        });
        defs.push({
          id: `${dept.id}:pendingTraining`,
          jsx: <PendingTrainingWidget dept={dept} data={data} deptColor={dc} isSupervisor={isSupervisor} empByIdAll={empByIdAll} />,
          empty: data.pendingTrainingList.length === 0,
          label: "Pending training",
          deptColor: dc, deptName: dept.name,
        });
        if (isSupervisor && betaCoverageRequests) {
          // Per-dept Coverage-requests widget for L2 — filtered to
          // requests for shifts in their home dept only. Beta-gated
          // while the feature is still in test.
          const requests = (coverageRequests.data ?? []).filter(
            (r) => r.departmentId === dept.id,
          );
          defs.push({
            id: `${dept.id}:coverageRequests`,
            jsx: (
              <CoverageRequestsWidget
                requests={requests}
                empByIdAll={empByIdAll}
                shiftsById={new Map((allShifts.data ?? []).map((s) => [s.id, s]))}
                deptsById={new Map((departments.data ?? []).map((d) => [d.id, d]))}
                onOpen={(r) => setReviewing(r)}
                deptColor={dc}
                deptName={dept.name}
              />
            ),
            empty: requests.length === 0,
            label: "Coverage requests",
            deptColor: dc, deptName: dept.name,
          });
        }
      }
    }
    return defs.filter(
      (d) => !hiddenWidgetCategories.has(widgetCategoryFromId(d.id)),
    );
  }, [
    scheduleWidgets, deptsToShow, deptDataByDeptId, departments.data,
    mayFix, setFixCoverage, isSupervisor, empByIdAll, showAllDepts,
    renderRequest, coverageRequests.data, allShifts.data,
    betaCoverageRequests, hiddenWidgetCategories,
  ]);

  const widgetById = useMemo(
    () => new Map(allFlatWidgets.map((w) => [w.id, w])),
    [allFlatWidgets],
  );

  // Normalize saved column assignments against the current widget
  // set: drop ids that no longer exist, dedupe across columns, and
  // append any brand-new widgets to whichever column is currently
  // shortest so a newly-appearing widget shows up without shoving
  // the user's arrangement around. This is derived state — the
  // saved assignments in `columnAssignments` are the source of
  // truth, `normalizedColumns` is what actually renders.
  const normalizedColumns = useMemo<string[][]>(() => {
    const valid = new Set(allFlatWidgets.map((w) => w.id));
    const seen = new Set<string>();
    const cols: string[][] = Array.from({ length: NUM_COLUMNS }, () => []);
    for (let c = 0; c < NUM_COLUMNS; c++) {
      const src = columnAssignments[c] ?? [];
      for (const id of src) {
        if (valid.has(id) && !seen.has(id)) {
          cols[c].push(id);
          seen.add(id);
        }
      }
    }
    // Append any missing widgets to the shortest column.
    for (const w of allFlatWidgets) {
      if (seen.has(w.id)) continue;
      let shortest = 0;
      for (let c = 1; c < NUM_COLUMNS; c++) {
        if (cols[c].length < cols[shortest].length) shortest = c;
      }
      cols[shortest].push(w.id);
      seen.add(w.id);
    }
    return cols;
  }, [allFlatWidgets, columnAssignments]);

  // Persist the normalized layout when the derivation actually
  // added or dropped an id (fresh forward, dept added, dept removed).
  // Only writes when the shape drifted so we don't loop on every
  // render.
  useEffect(() => {
    const same =
      columnAssignments.length === normalizedColumns.length
      && columnAssignments.every((col, i) =>
        col.length === normalizedColumns[i].length
        && col.every((id, j) => id === normalizedColumns[i][j]));
    if (!same) setColumnAssignments(normalizedColumns);
  }, [normalizedColumns, columnAssignments]);

  // Mobile fallback: flatten the 4-column layout back into a single
  // vertical stack in reading order (col 0 top to bottom, then col
  // 1, …). Preserves the user's arrangement intent while still
  // letting them drag-to-reorder within the single column.
  const mobileOrderedIds = useMemo(
    () => normalizedColumns.flat(),
    [normalizedColumns],
  );

  // Effective collapse state: a widget is collapsed by default when
  // it's empty. The two overrides can flip it either way. Toggling
  // clears whichever override is stale so the state stays clean.
  function isCollapsed(id: string): boolean {
    const w = widgetById.get(id);
    if (!w) return false;
    if (userExpandedIds.has(id)) return false;
    if (userCollapsedIds.has(id)) return true;
    return w.empty;
  }
  function toggleCollapsed(id: string) {
    const w = widgetById.get(id);
    if (!w) return;
    const currentlyCollapsed = isCollapsed(id);
    if (currentlyCollapsed) {
      // Expand: drop from userCollapsed if present, add to
      // userExpanded if the default would collapse it.
      setUserCollapsedIds((s) => {
        if (!s.has(id)) return s;
        const n = new Set(s); n.delete(id); return n;
      });
      if (w.empty) setUserExpandedIds((s) => new Set(s).add(id));
    } else {
      // Collapse: drop from userExpanded, add to userCollapsed if
      // the default would expand it.
      setUserExpandedIds((s) => {
        if (!s.has(id)) return s;
        const n = new Set(s); n.delete(id); return n;
      });
      if (!w.empty) setUserCollapsedIds((s) => new Set(s).add(id));
    }
  }

  // Drag-to-arrange across 4 columns. Each column is a droppable
  // SortableContext; drops within a column reorder, drops that
  // cross columns move the widget over. onDragOver handles the
  // cross-column case as the pointer moves so the placeholder
  // slides into the target column live; onDragEnd finalizes the
  // final within-column position.
  function findColumnOf(id: string, cols: string[][]): number {
    for (let i = 0; i < cols.length; i++) {
      if (cols[i].includes(id)) return i;
    }
    // If the id is one of our column droppable ids ("col:0" …),
    // parse it back out.
    if (id.startsWith("col:")) {
      const n = Number(id.slice(4));
      if (n >= 0 && n < cols.length) return n;
    }
    return -1;
  }

  function handleDragOver(activeId: string, overId: string) {
    setColumnAssignments((prev) => {
      const activeCol = findColumnOf(activeId, prev);
      const overCol = findColumnOf(overId, prev);
      if (activeCol === -1 || overCol === -1) return prev;
      if (activeCol === overCol) return prev; // handled in dragEnd
      const next = prev.map((c) => c.slice());
      const activeIdx = next[activeCol].indexOf(activeId);
      if (activeIdx === -1) return prev;
      next[activeCol].splice(activeIdx, 1);
      // If over target is the column droppable itself, append.
      // Otherwise insert at the over item's index.
      if (overId.startsWith("col:")) {
        next[overCol].push(activeId);
      } else {
        const overIdx = next[overCol].indexOf(overId);
        next[overCol].splice(overIdx >= 0 ? overIdx : next[overCol].length, 0, activeId);
      }
      return next;
    });
  }

  function handleDragEnd(activeId: string, overId: string) {
    setColumnAssignments((prev) => {
      const activeCol = findColumnOf(activeId, prev);
      const overCol = findColumnOf(overId, prev);
      if (activeCol === -1) return prev;
      if (activeCol !== overCol) return prev; // already handled in onDragOver
      const activeIdx = prev[activeCol].indexOf(activeId);
      const overIdx = overId.startsWith("col:")
        ? prev[overCol].length - 1
        : prev[overCol].indexOf(overId);
      if (activeIdx === -1 || overIdx === -1 || activeIdx === overIdx) return prev;
      const next = prev.map((c) => c.slice());
      next[activeCol] = arrayMove(next[activeCol], activeIdx, overIdx);
      return next;
    });
  }

  // While a widget is being dragged we render an extra trailing
  // empty column as a drop target so the user can drag a widget
  // out into a new column. When idle we drop that placeholder
  // column so the populated columns center cleanly without an
  // invisible w-96 shell shifting them off-center. Flipped by the
  // DndContext's onDragStart / onDragEnd on the desktop grid.
  const [dragActive, setDragActive] = useState(false);

  function handleMobileDragEnd(activeId: string, overId: string) {
    const flat = mobileOrderedIds;
    const oldIdx = flat.indexOf(activeId);
    const newIdx = flat.indexOf(overId);
    if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
    const reordered = arrayMove(flat, oldIdx, newIdx);
    // Pack the new flat order back into the first column, empties
    // for the rest. Users on mobile don't manage columns anyway.
    setColumnAssignments([
      reordered,
      [],
      [],
      [],
    ]);
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Dashboard
          </h1>
          <p className="text-sm text-slate-500">{new Date().toDateString()}</p>
        </div>
        {/* L2 / L3 / L4 are locked to their home department on the
          * dashboard now (no dropdown). Only Owner / L1 see every
          * dept, which they get via the flat list of DeptSections
          * below — so no dropdown is needed for them either. */}
      </header>



      {/* Flat widget pool — schedule + coverage widgets from every
        * dept sit in a 4-column grid the user can rearrange freely.
        * A widget can be dragged into any column, and expanded /
        * collapsed with the header chevron so the small ones can
        * stack under a tall one without wasting space.
        *
        * Mobile: single column stack (drag still works for reorder
        * within the stack). Desktop: 4 fixed columns, centered as a
        * group so a partial layout doesn't stretch across the
        * viewport. */}
      {allFlatWidgets.length > 0 && (
        <>
          {/* Mobile stack */}
          <div className="lg:hidden">
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={(e: DragEndEvent) => {
                const { active, over } = e;
                if (!over || active.id === over.id) return;
                handleMobileDragEnd(String(active.id), String(over.id));
              }}
            >
              <SortableContext
                items={mobileOrderedIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-4">
                  {mobileOrderedIds.map((id) => {
                    const w = widgetById.get(id);
                    if (!w) return null;
                    return (
                      <SortableWidget key={id} id={id}>
                        <CollapsibleWidget
                          label={w.label}
                          deptColor={w.deptColor}
                          deptName={w.deptName}
                          collapsed={isCollapsed(id)}
                          onToggle={() => toggleCollapsed(id)}
                        >
                          {w.jsx}
                        </CollapsibleWidget>
                      </SortableWidget>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Desktop 4-column grid */}
          <div className="hidden lg:flex lg:justify-center lg:gap-4">
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragStart={() => setDragActive(true)}
              onDragOver={(e) => {
                const { active, over } = e;
                if (!over || active.id === over.id) return;
                handleDragOver(String(active.id), String(over.id));
              }}
              onDragEnd={(e: DragEndEvent) => {
                setDragActive(false);
                const { active, over } = e;
                if (!over || active.id === over.id) return;
                handleDragEnd(String(active.id), String(over.id));
              }}
              onDragCancel={() => setDragActive(false)}
            >
              {/* Only render up to the last populated column when
                * idle, so a partial layout centers cleanly. During
                * a drag, add one trailing empty column as a drop
                * target so a widget can be dragged into a fresh
                * column. When all four are populated no trailing
                * placeholder appears (already at the cap). */}
              {(() => {
                let lastPopulated = -1;
                for (let i = normalizedColumns.length - 1; i >= 0; i--) {
                  if (normalizedColumns[i].length > 0) { lastPopulated = i; break; }
                }
                const idlePlus = dragActive ? 2 : 1;
                const visibleCount = Math.min(
                  NUM_COLUMNS,
                  Math.max(1, lastPopulated + idlePlus),
                );
                return normalizedColumns.slice(0, visibleCount);
              })().map((colIds, colIdx) => {
                return (
                  <DashboardColumn
                    key={colIdx}
                    colIdx={colIdx}
                    ids={colIds}
                  >
                    {colIds.map((id) => {
                      const w = widgetById.get(id);
                      if (!w) return null;
                      return (
                        <SortableWidget key={id} id={id}>
                          <CollapsibleWidget
                            label={w.label}
                            deptColor={w.deptColor}
                            deptName={w.deptName}
                            collapsed={isCollapsed(id)}
                            onToggle={() => toggleCollapsed(id)}
                          >
                            {w.jsx}
                          </CollapsibleWidget>
                        </SortableWidget>
                      );
                    })}
                  </DashboardColumn>
                );
              })}
            </DndContext>
          </div>
        </>
      )}

      {/* Per-dept callouts below the grid: Owner / L1 training-
        * awaiting-final banner and the pending-forms strip. Only
        * render when there is something to show, per dept. */}
      {deptsToShow.map((dept) => {
        const data = deptDataByDeptId.get(dept.id);
        if (!data) return null;
        return (
          <DeptCallouts
            key={dept.id}
            dept={dept}
            data={data}
            isSupervisor={isSupervisor}
            empByIdAll={empByIdAll}
          />
        );
      })}

      {fixCoverage && (() => {
        const sh = (allShifts.data ?? []).find((s) => s.id === fixCoverage.shiftId);
        if (!sh) return null;
        return (
          <FixCoverageModal
            shift={sh}
            date={fixCoverage.date}
            onClose={() => setFixCoverage(null)}
          />
        );
      })()}

      {reviewing && (() => {
        const shift = (allShifts.data ?? []).find((s) => s.id === reviewing.shiftId) ?? null;
        const requester = empByIdAll.get(reviewing.requesterEmployeeId) ?? null;
        return (
          <ReviewCoverageRequestModal
            request={reviewing}
            shift={shift}
            requester={requester}
            onClose={() => setReviewing(null)}
          />
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------
// Dashboard layout primitives — column droppable + collapsible
// widget wrapper used by the arrangeable 4-column grid above.

// One column in the desktop 4-column grid. Each column is its own
// droppable + SortableContext so a widget can be dragged into an
// empty column and dropped there. Fixed width (w-96 / 24rem) for
// every column so the coverage widgets and the day-by-day
// schedule read at the same size and can be swapped between
// columns without one column suddenly looking narrower than
// its neighbour.
function DashboardColumn({
  colIdx, ids, children,
}: {
  colIdx: number;
  ids: string[];
  children: ReactNode;
}) {
  const droppableId = `col:${colIdx}`;
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  return (
    <SortableContext id={droppableId} items={ids} strategy={verticalListSortingStrategy}>
      <div
        ref={setNodeRef}
        className={
          "flex flex-col gap-4 min-h-[80px] rounded-lg transition-colors w-96 "
          + (isOver && ids.length === 0
            ? "bg-slate-100/60 border-2 border-dashed border-slate-300"
            : "")
        }
      >
        {children}
      </div>
    </SortableContext>
  );
}

// Wraps a widget with a header strip carrying a dept badge, the
// widget's label, and a chevron button that toggles the collapsed
// state. Collapsed widgets show only the header so the user can
// still read where they are in the grid. Empty widgets default to
// collapsed, non-empty default to expanded — either can be flipped
// per widget.
function CollapsibleWidget({
  label, deptColor, deptName, collapsed, onToggle, children,
}: {
  label: string;
  deptColor: { border: string; dot: string } | null;
  deptName: string | null;
  collapsed: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      {collapsed ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          onPointerDown={(e) => e.stopPropagation()}
          className={
            "w-full flex items-center justify-between gap-3 rounded-lg bg-white "
            + "border shadow-sm px-3 py-2 text-left hover:bg-slate-50 "
            + (deptColor?.border ?? "border-slate-200")
          }
        >
          <div className="flex items-center gap-2 min-w-0">
            {deptColor && (
              <span className={"inline-block h-2 w-2 rounded-full " + deptColor.dot} />
            )}
            <span className="text-sm font-medium text-slate-800 truncate">
              {label}
            </span>
            {deptName && (
              <span className="text-xs text-slate-500 truncate">
                · {deptName}
              </span>
            )}
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
            fill="currentColor" className="h-4 w-4 text-slate-400 flex-shrink-0"
            aria-hidden="true"
          >
            <path fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 011.08 1.04l-4.25 4.4a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd" />
          </svg>
        </button>
      ) : (
        <>
          {children}
          {/* Collapse chevron overlaid on the expanded card. Positioned
            * top-right so it doesn't fight the widget's own header. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute top-2 right-2 z-10 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
            aria-label="Collapse widget"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
              fill="currentColor" className="h-4 w-4"
              aria-hidden="true"
            >
              <path fillRule="evenodd"
                d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.94l-3.71 3.83a.75.75 0 11-1.08-1.04l4.25-4.4a.75.75 0 011.08 0l4.25 4.4a.75.75 0 01-.02 1.06z"
                clipRule="evenodd" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// "My schedule" widget helpers — the day-by-day 2-week widget the
// officer sees at the top of their dashboard.

type MyScheduleDay = {
  date: string;
  weekdayLabel: string;
  monthDayLabel: string;
  kind: "work" | "time_off" | "day_off" | "none";
  isOT: boolean;
  primary: string;
  hours: number | null;
  shiftName?: string;
  shiftColor?: string;
};

function buildMyScheduleDay(
  date: string,
  myAssignments: Assignment[],
  myTimeOff: TimeOff[],
  shiftById: Map<string, Shift>,
  codeMap: Map<string, ShiftCode>,
  // Home shift used as a silent-day fallback: when the officer has
  // no explicit assignment recorded for this date, the widget still
  // shows the shift's default hours (e.g. "06:00–14:00") rather
  // than a dash. Null when the officer has no home shift.
  fallbackShift: Shift | null,
): MyScheduleDay {
  const d = new Date(date + "T00:00:00");
  const weekdayLabel = d.toLocaleDateString(undefined, { weekday: "short" });
  const monthDayLabel = d.toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  });

  // 1. Approved time-off overlapping this date wins over everything —
  // no matter what the schedule grid shows.
  const timeOffMatch = myTimeOff.find(
    (t) => t.startDate <= date && date <= t.endDate,
  );
  if (timeOffMatch) {
    return {
      date, weekdayLabel, monthDayLabel,
      kind: "time_off", isOT: false,
      primary: TIME_OFF_LABEL[timeOffMatch.type] ?? "Time off",
      hours: null,
    };
  }

  // 2. Assignment(s) for this date. Multiple rows can share a date
  // (a home-shift row plus an OT row from another shift, or a
  // Full-OT filling for someone else). Prefer the OT row when one
  // exists so the widget can badge OT and show the OT hours.
  const dayRows = myAssignments.filter((a) => a.date === date);
  const otRow = dayRows.find(
    (a) => (a.overrideReason ?? "").toLowerCase().startsWith("overtime"),
  );
  const baseRow = dayRows.find((a) => a !== otRow) ?? dayRows[0];
  const primaryRow = otRow ?? baseRow;

  if (!primaryRow) {
    // No explicit assignment — fall back to the officer's home
    // shift so a silent working day still reads as its default
    // hours (e.g. Day shift 06:00–14:00). Only if the officer has
    // one; otherwise a dash.
    if (fallbackShift) {
      const s = hourOf(fallbackShift.startTime);
      if (s !== null) {
        const endH = (s + Math.round(fallbackShift.durationMin / 60)) % 24;
        const hh = (h: number) => String(h).padStart(2, "0") + ":00";
        const hours = endH === s ? 24 : (endH > s ? endH - s : 24 - s + endH);
        return {
          date, weekdayLabel, monthDayLabel,
          kind: "work", isOT: false,
          primary: `${hh(s)}–${hh(endH)}`,
          hours,
          shiftName: fallbackShift.name,
          shiftColor: fallbackShift.color ?? undefined,
        };
      }
    }
    return {
      date, weekdayLabel, monthDayLabel,
      kind: "none", isOT: false,
      primary: "—",
      hours: null,
    };
  }

  const shift = shiftById.get(primaryRow.shiftId);
  const note = (primaryRow.note ?? "").trim().toUpperCase();

  // 3. Off-code note (XXX / PL / S / V …). Coverage = 0 means the
  // officer isn't working. Custom hour notes ("9-21") fall through
  // to the "work" branch below.
  if (note && !otRow) {
    const code = codeMap.get(note);
    if (code && code.coverageValue === 0) {
      const label = code.code === "XXX" ? "Day off" : (code.label || code.code);
      return {
        date, weekdayLabel, monthDayLabel,
        kind: "day_off", isOT: false,
        primary: label,
        hours: null,
      };
    }
    // Letter-only note not in the codes table = treat as off.
    if (/^[A-Z]+$/.test(note) && !code) {
      return {
        date, weekdayLabel, monthDayLabel,
        kind: "day_off", isOT: false,
        primary: note,
        hours: null,
      };
    }
  }

  // 4. Working. Prefer OT-specific hours when present, then the
  // note's parsed hours, then the shift's own start/end.
  let startH: number | null = null;
  let endH: number | null = null;
  if (otRow && otRow.otStartTime && otRow.otEndTime) {
    startH = hourOf(otRow.otStartTime);
    endH = hourOf(otRow.otEndTime);
  } else if (note) {
    const parsed = parseHourNote(note);
    if (parsed) {
      startH = parsed[0];
      endH = parsed[1];
    }
  }
  if ((startH === null || endH === null) && shift) {
    const s = hourOf(shift.startTime);
    if (s !== null) {
      startH = s;
      // Shift stores start + durationMin (there's no endTime field);
      // derive the end hour by adding whole hours from the duration.
      endH = (s + Math.round(shift.durationMin / 60)) % 24;
    }
  }

  const hh = (h: number) => String(h).padStart(2, "0") + ":00";
  const hours = startH !== null && endH !== null
    ? (endH === startH ? 24 : (endH > startH ? endH - startH : 24 - startH + endH))
    : null;

  return {
    date, weekdayLabel, monthDayLabel,
    kind: "work",
    isOT: !!otRow,
    primary: startH !== null && endH !== null
      ? `${hh(startH)}–${hh(endH)}`
      : shift?.name ?? "Scheduled",
    hours,
    shiftName: shift?.name,
    shiftColor: shift?.color ?? undefined,
  };
}

function MyScheduleWidget({ title, days }: { title: string; days: MyScheduleDay[] }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <h3 className="font-medium text-slate-900 mb-3">{title}</h3>
      <ul className="divide-y divide-slate-100 text-sm">
        {days.map((d) => {
          const totalLabel = d.hours != null
            ? `${d.hours}h`
            : "";
          return (
            <li
              key={d.date}
              className="py-1.5 grid grid-cols-[6rem_1fr_auto] items-center gap-3"
            >
              <div className="text-slate-700">
                <span className="font-medium">{d.weekdayLabel}</span>{" "}
                <span className="text-slate-500">{d.monthDayLabel}</span>
              </div>
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                {d.kind === "work" && d.shiftName && (
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: d.shiftColor ?? "#64748b" }}
                    title={d.shiftName}
                  />
                )}
                <span className={
                  d.kind === "time_off"
                    ? "text-sky-700 font-medium"
                    : d.kind === "day_off"
                      ? "text-slate-500"
                      : d.kind === "none"
                        ? "text-slate-400"
                        : "text-slate-800 font-medium"
                }>
                  {d.primary}
                </span>
                {d.isOT && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border bg-amber-100 text-amber-800 border-amber-200">
                    OT
                  </span>
                )}
              </div>
              <div className="text-slate-500 font-mono text-xs whitespace-nowrap tabular-nums">
                {totalLabel}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------
// Per-department color assignment. The dashboard tints each dept's
// widgets with its own accent so the multi-dept view (Owner / L1)
// reads clearly across a stack of sections. Colors are assigned
// procedurally by the dept's sortOrder / list position — no DB
// column to manage. Every string here is a literal Tailwind class
// so the JIT picks them up.

const DEPT_PALETTE: Array<{ border: string; dot: string }> = [
  { border: "border-l-sky-500",     dot: "bg-sky-500" },
  { border: "border-l-amber-500",   dot: "bg-amber-500" },
  { border: "border-l-emerald-500", dot: "bg-emerald-500" },
  { border: "border-l-purple-500",  dot: "bg-purple-500" },
  { border: "border-l-pink-500",    dot: "bg-pink-500" },
  { border: "border-l-indigo-500",  dot: "bg-indigo-500" },
  { border: "border-l-teal-500",    dot: "bg-teal-500" },
  { border: "border-l-orange-500",  dot: "bg-orange-500" },
];
const DEPT_NEUTRAL = { border: "border-l-slate-300", dot: "bg-slate-400" };

function colorForDept(dept: Department, departments: Department[]) {
  const sorted = [...departments].sort((a, b) =>
    (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
    || a.name.localeCompare(b.name));
  const idx = sorted.findIndex((d) => d.id === dept.id);
  if (idx < 0) return DEPT_NEUTRAL;
  return DEPT_PALETTE[idx % DEPT_PALETTE.length];
}

// ---------------------------------------------------------------
// Pure per-dept compute helper. Same logic that used to run inside
// DeptSection's useMemos, hoisted so Dashboard can iterate depts
// in one useMemo and hand each widget the pre-computed slice it
// needs.

type DeptComputed = {
  employees: Employee[];
  visibleShifts: Shift[];
  understaffed: ShiftCoverage[];
  understaffedByShift: Array<{
    shiftId: string; shiftName: string; shiftColor: string;
    shiftSort: number; deptName: string; deptSort: number;
    items: ShiftCoverage[];
  }>;
  overstaffed: ShiftCoverage[];
  overstaffedByShift: Array<{
    shiftId: string; shiftName: string; shiftColor: string;
    shiftSort: number; deptName: string; deptSort: number;
    items: ShiftCoverage[];
  }>;
  trainingAwaitingFinal: TimeOff[];
  pendingList: TimeOff[];
  pendingTimeOffList: TimeOff[];
  pendingTrainingList: TimeOff[];
  pendingByForm: Array<{ form: Form; items: TimeOff[] }>;
  pendingFormsTotal: number;
  empById: Map<string, Employee>;
  deptEmpIds: Set<string>;
};

function computeDeptCoverage(args: {
  dept: Department;
  departments: Department[];
  allEmployees: Employee[];
  allShifts: Shift[];
  allCoverage: ShiftCoverage[];
  allWindowAssignments: Assignment[];
  allPending: TimeOff[];
  allForms: Form[];
  disabledFeatures: Set<string>;
  agency: Agency | null;
  me: User | null;
  isSupervisor: boolean;
}): DeptComputed {
  const {
    dept, departments, allEmployees, allShifts, allCoverage,
    allWindowAssignments, allPending, allForms, disabledFeatures,
    agency, me, isSupervisor,
  } = args;
  const employees = allEmployees.filter((e) => e.departmentId === dept.id);
  const rawShifts = allShifts.filter((s) => s.departmentId === dept.id);
  const visibleShifts = applyTacOverlay(agency, rawShifts, employees).visibleShifts;
  const rotationShiftIds = new Set(
    visibleShifts.filter((s) => s.inRotation !== false).map((s) => s.id),
  );
  const shiftById = new Map(visibleShifts.map((s) => [s.id, s]));
  const rawShiftIds = new Set(rawShifts.map((s) => s.id));
  const deptCoverageRows = allCoverage.filter((c) => rawShiftIds.has(c.shiftId));

  const understaffed = deptCoverageRows.filter((c) => {
    if (!rotationShiftIds.has(c.shiftId)) return false;
    const sh = shiftById.get(c.shiftId);
    if (sh && !isShiftActiveOn(agency, sh, c.date)) return false;
    return c.assigned < c.minStaffing || c.oicAssigned < c.oicRequired;
  });

  const deptById = new Map(departments.map((d) => [d.id, d]));
  function groupByShift(rows: ShiftCoverage[]) {
    const groups = new Map<string, {
      shiftId: string; shiftName: string; shiftColor: string;
      shiftSort: number; deptName: string; deptSort: number;
      items: ShiftCoverage[];
    }>();
    for (const c of rows) {
      const sh = shiftById.get(c.shiftId);
      if (!sh) continue;
      const dp = sh.departmentId ? deptById.get(sh.departmentId) : undefined;
      const g = groups.get(sh.id) ?? {
        shiftId: sh.id,
        shiftName: sh.name,
        shiftColor: sh.color ?? "#64748b",
        shiftSort: sh.sortOrder ?? 999,
        deptName: dp?.name ?? "Unassigned",
        deptSort: dp?.sortOrder ?? 999,
        items: [] as ShiftCoverage[],
      };
      g.items.push(c);
      groups.set(sh.id, g);
    }
    return [...groups.values()].sort((a, b) =>
      a.deptSort - b.deptSort
      || a.shiftSort - b.shiftSort
      || a.shiftName.localeCompare(b.shiftName));
  }
  const understaffedByShift = groupByShift(understaffed);

  const visibleShiftIds = new Set(visibleShifts.map((s) => s.id));
  const otShiftDateKeys = new Set<string>();
  for (const a of allWindowAssignments) {
    if (!visibleShiftIds.has(a.shiftId)) continue;
    const reason = (a.overrideReason ?? "").toLowerCase();
    if (reason.startsWith("overtime")) otShiftDateKeys.add(`${a.shiftId}|${a.date}`);
  }
  const overstaffed = deptCoverageRows.filter((c) => {
    if (!rotationShiftIds.has(c.shiftId)) return false;
    const sh = shiftById.get(c.shiftId);
    if (sh && !isShiftActiveOn(agency, sh, c.date)) return false;
    if (c.assigned <= c.minStaffing) return false;
    return otShiftDateKeys.has(`${c.shiftId}|${c.date}`);
  });
  const overstaffedByShift = groupByShift(overstaffed);

  const deptEmpIds = new Set(employees.map((e) => e.id));
  const trainingAwaitingFinal = (me?.level === "owner" || me?.level === "l1")
    ? allPending
        .filter((t) => t.type === "training" && t.status === "l2_approved" && deptEmpIds.has(t.employeeId))
        .slice()
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
    : [];
  const empById = new Map(employees.map((e) => [e.id, e]));

  const isPending = (t: TimeOff) =>
    t.status === "requested"
    || (t.type === "training" && t.status === "l2_approved");
  const scoped = isSupervisor
    ? allPending.filter((t) => deptEmpIds.has(t.employeeId) && isPending(t))
    : allPending.filter((t) => t.employeeId === me?.employeeId && isPending(t));
  const pendingList = scoped.slice().sort((a, b) => a.startDate.localeCompare(b.startDate));
  const pendingTimeOffList = pendingList.filter((t) => t.type !== "training" && !t.formSlug);
  const pendingTrainingList = pendingList.filter((t) => t.type === "training" && !t.formSlug);

  const formsBySlug = new Map(allForms.map((f) => [f.slug, f]));
  const formGroups = new Map<string, { form: Form; items: TimeOff[] }>();
  for (const t of pendingList) {
    if (!t.formSlug) continue;
    if (disabledFeatures.has(t.formSlug)) continue;
    const form = formsBySlug.get(t.formSlug);
    if (!form || form.builtin) continue;
    const g = formGroups.get(t.formSlug) ?? { form, items: [] as TimeOff[] };
    g.items.push(t);
    formGroups.set(t.formSlug, g);
  }
  const pendingByForm = [...formGroups.values()].sort((a, b) =>
    a.form.name.localeCompare(b.form.name));
  const pendingFormsTotal = pendingByForm.reduce((n, g) => n + g.items.length, 0);

  return {
    employees, visibleShifts, understaffed, understaffedByShift,
    overstaffed, overstaffedByShift, trainingAwaitingFinal,
    pendingList, pendingTimeOffList, pendingTrainingList,
    pendingByForm, pendingFormsTotal, empById, deptEmpIds,
  };
}

// ---------------------------------------------------------------
// Widget components. Each takes the dept it belongs to, the
// pre-computed data slice, its dept color, and any callbacks it
// needs. Widget JSX is unchanged from what used to live inside
// DeptSection — the local `const understaffed = data.understaffed`
// aliases at the top let the JSX read exactly as it did before.

function UnderstaffedWidget({
  dept, data, deptColor, mayFix, setFixCoverage, renderRequest,
}: {
  dept: Department;
  data: DeptComputed;
  deptColor: { border: string; dot: string };
  mayFix: boolean;
  setFixCoverage: (v: { shiftId: string; date: string } | null) => void;
  renderRequest?: (shiftId: string, deptId: string, date: string) => React.ReactNode;
}) {
  const understaffed = data.understaffed;
  const understaffedByShift = data.understaffedByShift;
  return (
<div className={"bg-white border border-slate-200 rounded-lg p-4 border-l-4 " + deptColor.border}>
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <span className={"inline-block w-1.5 h-1.5 rounded-full " + deptColor.dot} />
            {dept.name}
          </div>
          <div className="mb-3">
            <h3 className="font-medium text-slate-900">Understaffed shifts</h3>
          </div>
          {understaffed.length === 0 ? (
            <p className="text-sm text-slate-500">All shifts meet minimum staffing.</p>
          ) : (
            <div className="space-y-5 text-sm">
              {understaffedByShift.map((g) => (
                <div
                  key={g.shiftId}
                  className="rounded border border-slate-200 overflow-hidden"
                  style={{ borderLeft: `4px solid ${g.shiftColor}` }}
                >
                  <div
                    className="themed-fill px-3 py-1.5 flex items-center justify-between text-[11px] uppercase tracking-wider"
                    style={{
                      backgroundColor: g.shiftColor,
                      ["--themed-color" as string]: g.shiftColor,
                      color: readableTextOn(g.shiftColor),
                    }}
                  >
                    <span className="font-semibold">
                      {g.shiftName}
                      <span
                        className="ml-2 normal-case tracking-normal opacity-80 dark:opacity-70"
                        style={{
                          color: readableTextOn(g.shiftColor) === "#ffffff"
                            ? "rgba(255,255,255,0.78)"
                            : "rgba(15,23,42,0.65)",
                        }}
                      >
                        · {g.deptName}
                      </span>
                    </span>
                    <span
                      className="opacity-80 dark:opacity-70"
                      style={{
                        color: readableTextOn(g.shiftColor) === "#ffffff"
                          ? "rgba(255,255,255,0.78)"
                          : "rgba(15,23,42,0.65)",
                      }}
                    >
                      {g.items.length} short
                    </span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {g.items.map((c) => {
                      const totalShort = c.assigned < c.minStaffing;
                      const oicShort = c.oicAssigned < c.oicRequired;
                      const body = (
                        <>
                          <span>{c.date}</span>
                          <span className="text-amber-700 font-medium whitespace-nowrap">
                            {totalShort && (
                              <span>{c.assigned} / {c.minStaffing} staff</span>
                            )}
                            {totalShort && oicShort && <span className="mx-1">·</span>}
                            {oicShort && (
                              <span>{c.oicAssigned} / {c.oicRequired} OIC</span>
                            )}
                          </span>
                        </>
                      );
                      const requestBtn = renderRequest?.(c.shiftId, dept.id, c.date);
                      const rowInner = mayFix ? (
                        <button
                          type="button"
                          className="flex-1 py-2 px-3 flex justify-between gap-3 hover:bg-slate-50 text-left min-w-0"
                          onClick={() => setFixCoverage({ shiftId: c.shiftId, date: c.date })}
                          title="Click to fix coverage"
                        >
                          {body}
                        </button>
                      ) : (
                        <div className="flex-1 py-2 px-3 flex justify-between gap-3 min-w-0">
                          {body}
                        </div>
                      );
                      return (
                        <li key={c.date + c.shiftId} className="py-0 flex items-center gap-2 pr-3">
                          {rowInner}
                          {requestBtn}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
  );
}

function OverstaffedWidget({
  dept, data, deptColor,
}: {
  dept: Department;
  data: DeptComputed;
  deptColor: { border: string; dot: string };
}) {
  const overstaffed = data.overstaffed;
  const overstaffedByShift = data.overstaffedByShift;
  const navigate = useNavigate();
  return (
<div className={"bg-white border border-slate-200 rounded-lg p-4 border-l-4 " + deptColor.border}>
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <span className={"inline-block w-1.5 h-1.5 rounded-full " + deptColor.dot} />
            {dept.name}
          </div>
          <div className="mb-3">
            <h3 className="font-medium text-slate-900">
              Overstaffed shifts
              {overstaffed.length > 0 && (
                <span className="ml-2 text-slate-500 font-normal">
                  ({overstaffed.length})
                </span>
              )}
            </h3>
          </div>
          {overstaffed.length === 0 ? (
            <p className="text-sm text-slate-500">
              No overstaffed shifts in this window.
            </p>
          ) : (
            <div className="space-y-5 text-sm">
              {overstaffedByShift.map((g) => (
                <div
                  key={g.shiftId}
                  className="rounded border border-slate-200 overflow-hidden"
                  style={{ borderLeft: `4px solid ${g.shiftColor}` }}
                >
                  <div
                    className="themed-fill px-3 py-1.5 flex items-center justify-between text-[11px] uppercase tracking-wider"
                    style={{
                      backgroundColor: g.shiftColor,
                      ["--themed-color" as string]: g.shiftColor,
                      color: readableTextOn(g.shiftColor),
                    }}
                  >
                    <span className="font-semibold">
                      {g.shiftName}
                      <span
                        className="ml-2 normal-case tracking-normal opacity-80 dark:opacity-70"
                        style={{
                          color: readableTextOn(g.shiftColor) === "#ffffff"
                            ? "rgba(255,255,255,0.78)"
                            : "rgba(15,23,42,0.65)",
                        }}
                      >
                        · {g.deptName}
                      </span>
                    </span>
                    <span
                      className="opacity-80 dark:opacity-70"
                      style={{
                        color: readableTextOn(g.shiftColor) === "#ffffff"
                          ? "rgba(255,255,255,0.78)"
                          : "rgba(15,23,42,0.65)",
                      }}
                    >
                      {g.items.length} over
                    </span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {g.items.map((c) => (
                      <li key={c.date + c.shiftId} className="py-0">
                        <button
                          type="button"
                          className="w-full py-2 px-3 flex justify-between gap-3 hover:bg-slate-50 text-left"
                          onClick={() => navigate(`/schedule/${dept.id}?anchor=${c.date}`)}
                          title="Open this day on the schedule"
                        >
                          <span>{c.date}</span>
                          <span className="text-emerald-700 font-semibold whitespace-nowrap">
                            {c.assigned} / {c.minStaffing} staff
                            <span className="ml-1 text-emerald-600">
                              (+{c.assigned - c.minStaffing})
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
  );
}

function PendingTimeOffWidget({
  dept, data, deptColor, isSupervisor, empByIdAll,
}: {
  dept: Department;
  data: DeptComputed;
  deptColor: { border: string; dot: string };
  isSupervisor: boolean;
  empByIdAll: Map<string, Employee>;
}) {
  const pendingTimeOffList = data.pendingTimeOffList;
  return (
<div className={"bg-white border border-slate-200 rounded-lg p-4 border-l-4 " + deptColor.border}>
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <span className={"inline-block w-1.5 h-1.5 rounded-full " + deptColor.dot} />
            {dept.name}
          </div>
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="font-medium text-slate-900">
              {isSupervisor ? "Pending time off" : "My pending time off"}
              {pendingTimeOffList.length > 0 && (
                <span className="ml-2 text-slate-500 font-normal">
                  ({pendingTimeOffList.length})
                </span>
              )}
            </h3>
            {pendingTimeOffList.length > 0 && (
              <a
                href="#/time-off"
                className="text-xs font-medium text-sky-700 hover:underline whitespace-nowrap"
              >
                Time Off →
              </a>
            )}
          </div>
          {pendingTimeOffList.length === 0 ? (
            <p className="text-sm text-slate-500">
              {isSupervisor
                ? `No pending time off in ${dept.name}.`
                : "You have no pending time off."}
            </p>
          ) : (
            <ul className="text-sm divide-y divide-slate-100">
              {pendingTimeOffList.slice(0, 8).map((t) => {
                const emp = empByIdAll.get(t.employeeId);
                const typeCode = TIME_OFF_CODE[t.type];
                const typeName = TIME_OFF_LABEL[t.type];
                const sameDay = t.startDate === t.endDate;
                return (
                  <li key={t.id} className="py-2 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border bg-sky-100 text-sky-800 border-sky-200"
                          title={typeName}
                        >
                          {typeCode}
                        </span>
                        {isSupervisor && (
                          <span className="font-medium text-slate-900 truncate">
                            {emp ? `${emp.lastName}, ${emp.firstName}` : t.employeeId}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {sameDay ? t.startDate : `${t.startDate} → ${t.endDate}`}
                      </div>
                    </div>
                  </li>
                );
              })}
              {pendingTimeOffList.length > 8 && (
                <li className="pt-2 text-xs text-slate-500 italic">
                  +{pendingTimeOffList.length - 8} more
                </li>
              )}
            </ul>
          )}
        </div>
  );
}

function PendingTrainingWidget({
  dept, data, deptColor, isSupervisor, empByIdAll,
}: {
  dept: Department;
  data: DeptComputed;
  deptColor: { border: string; dot: string };
  isSupervisor: boolean;
  empByIdAll: Map<string, Employee>;
}) {
  const pendingTrainingList = data.pendingTrainingList;
  return (
<div className={"bg-white border border-slate-200 rounded-lg p-4 border-l-4 " + deptColor.border}>
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <span className={"inline-block w-1.5 h-1.5 rounded-full " + deptColor.dot} />
            {dept.name}
          </div>
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="font-medium text-slate-900">
              {isSupervisor ? "Pending training" : "My pending training"}
              {pendingTrainingList.length > 0 && (
                <span className="ml-2 text-slate-500 font-normal">
                  ({pendingTrainingList.length})
                </span>
              )}
            </h3>
            {pendingTrainingList.length > 0 && (
              <a
                href="#/training"
                className="text-xs font-medium text-sky-700 hover:underline whitespace-nowrap"
              >
                Training →
              </a>
            )}
          </div>
          {pendingTrainingList.length === 0 ? (
            <p className="text-sm text-slate-500">
              {isSupervisor
                ? `No pending training in ${dept.name}.`
                : "You have no pending training."}
            </p>
          ) : (
            <ul className="text-sm divide-y divide-slate-100">
              {pendingTrainingList.slice(0, 8).map((t) => {
                const emp = empByIdAll.get(t.employeeId);
                const sameDay = t.startDate === t.endDate;
                return (
                  <li key={t.id} className="py-2 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {isSupervisor && (
                        <div className="font-medium text-slate-900 truncate">
                          {emp ? `${emp.lastName}, ${emp.firstName}` : t.employeeId}
                        </div>
                      )}
                      <div className="text-xs text-slate-500 mt-0.5">
                        {sameDay ? t.startDate : `${t.startDate} → ${t.endDate}`}
                        {t.status === "l2_approved" && (
                          <span className="ml-2 text-amber-700 font-medium">
                            · L2 approved, awaiting final
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
              {pendingTrainingList.length > 8 && (
                <li className="pt-2 text-xs text-slate-500 italic">
                  +{pendingTrainingList.length - 8} more
                </li>
              )}
            </ul>
          )}
        </div>
  );
}

// ---------------------------------------------------------------
// Consolidated widgets used on the Owner / L1 dashboard: one card
// per widget type, with a dept-labelled section inside for every
// dept that has data. Replaces the 4×N per-dept card grid so a
// supervisor scanning "everything understaffed today" reads one
// widget instead of N.

type DeptEntry = {
  dept: Department;
  data: DeptComputed;
  deptColor: { border: string; dot: string };
};

// Small dept-label strip used at the top of each section inside a
// consolidated widget. Matches the per-dept widget's badge (dot +
// dept name) so the visual language is the same in both modes.
function DeptSectionHeader({
  dept, deptColor,
}: {
  dept: Department;
  deptColor: { border: string; dot: string };
}) {
  return (
    <div className={"flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 pb-1 mb-2 border-b " + deptColor.border.replace("border-l-", "border-").replace("border-", "border-").replace("-500", "-200")}>
      <span className={"inline-block w-1.5 h-1.5 rounded-full " + deptColor.dot} />
      {dept.name}
    </div>
  );
}

function AllUnderstaffedWidget({
  entries, mayFix, setFixCoverage, renderRequest,
}: {
  entries: DeptEntry[];
  mayFix: boolean;
  setFixCoverage: (v: { shiftId: string; date: string } | null) => void;
  renderRequest?: (shiftId: string, deptId: string, date: string) => React.ReactNode;
}) {
  const totalCount = entries.reduce((sum, e) => sum + e.data.understaffed.length, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="mb-3">
        <h3 className="font-medium text-slate-900">
          Understaffed shifts
          {totalCount > 0 && (
            <span className="ml-2 text-slate-500 font-normal">({totalCount})</span>
          )}
        </h3>
      </div>
      {totalCount === 0 ? (
        <p className="text-sm text-slate-500">All departments meet minimum staffing.</p>
      ) : (
        <div className="space-y-5">
          {entries.map(({ dept, data, deptColor }) => {
            if (data.understaffedByShift.length === 0) return null;
            return (
              <div key={dept.id}>
                <DeptSectionHeader dept={dept} deptColor={deptColor} />
                <div className="space-y-3 text-sm">
                  {data.understaffedByShift.map((g) => (
                    <div
                      key={g.shiftId}
                      className="rounded border border-slate-200 overflow-hidden"
                      style={{ borderLeft: `4px solid ${g.shiftColor}` }}
                    >
                      <div
                        className="themed-fill px-3 py-1.5 flex items-center justify-between text-[11px] uppercase tracking-wider"
                        style={{
                          backgroundColor: g.shiftColor,
                      ["--themed-color" as string]: g.shiftColor,
                          color: readableTextOn(g.shiftColor),
                        }}
                      >
                        <span className="font-semibold">{g.shiftName}</span>
                        <span
                          className="opacity-80 dark:opacity-70"
                          style={{
                            color: readableTextOn(g.shiftColor) === "#ffffff"
                              ? "rgba(255,255,255,0.78)"
                              : "rgba(15,23,42,0.65)",
                          }}
                        >
                          {g.items.length} short
                        </span>
                      </div>
                      <ul className="divide-y divide-slate-100">
                        {g.items.map((c) => {
                          const totalShort = c.assigned < c.minStaffing;
                          const oicShort = c.oicAssigned < c.oicRequired;
                          const body = (
                            <>
                              <span>{c.date}</span>
                              <span className="text-amber-700 font-medium whitespace-nowrap">
                                {totalShort && <span>{c.assigned} / {c.minStaffing} staff</span>}
                                {totalShort && oicShort && <span className="mx-1">·</span>}
                                {oicShort && <span>{c.oicAssigned} / {c.oicRequired} OIC</span>}
                              </span>
                            </>
                          );
                          const requestBtn = renderRequest?.(c.shiftId, dept.id, c.date);
                          const rowInner = mayFix ? (
                            <button
                              type="button"
                              className="flex-1 py-2 px-3 flex justify-between gap-3 hover:bg-slate-50 text-left min-w-0"
                              onClick={() => setFixCoverage({ shiftId: c.shiftId, date: c.date })}
                              title="Click to fix coverage"
                            >
                              {body}
                            </button>
                          ) : (
                            <div className="flex-1 py-2 px-3 flex justify-between gap-3 min-w-0">
                              {body}
                            </div>
                          );
                          return (
                            <li key={c.date + c.shiftId} className="py-0 flex items-center gap-2 pr-3">
                              {rowInner}
                              {requestBtn}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AllOverstaffedWidget({ entries }: { entries: DeptEntry[] }) {
  const totalCount = entries.reduce((sum, e) => sum + e.data.overstaffed.length, 0);
  const navigate = useNavigate();
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="mb-3">
        <h3 className="font-medium text-slate-900">
          Overstaffed shifts
          {totalCount > 0 && (
            <span className="ml-2 text-slate-500 font-normal">({totalCount})</span>
          )}
        </h3>
      </div>
      {totalCount === 0 ? (
        <p className="text-sm text-slate-500">No overstaffed shifts in this window.</p>
      ) : (
        <div className="space-y-5">
          {entries.map(({ dept, data, deptColor }) => {
            if (data.overstaffedByShift.length === 0) return null;
            return (
              <div key={dept.id}>
                <DeptSectionHeader dept={dept} deptColor={deptColor} />
                <div className="space-y-3 text-sm">
                  {data.overstaffedByShift.map((g) => (
                    <div
                      key={g.shiftId}
                      className="rounded border border-slate-200 overflow-hidden"
                      style={{ borderLeft: `4px solid ${g.shiftColor}` }}
                    >
                      <div
                        className="themed-fill px-3 py-1.5 flex items-center justify-between text-[11px] uppercase tracking-wider"
                        style={{
                          backgroundColor: g.shiftColor,
                      ["--themed-color" as string]: g.shiftColor,
                          color: readableTextOn(g.shiftColor),
                        }}
                      >
                        <span className="font-semibold">{g.shiftName}</span>
                        <span
                          className="opacity-80 dark:opacity-70"
                          style={{
                            color: readableTextOn(g.shiftColor) === "#ffffff"
                              ? "rgba(255,255,255,0.78)"
                              : "rgba(15,23,42,0.65)",
                          }}
                        >
                          {g.items.length} over
                        </span>
                      </div>
                      <ul className="divide-y divide-slate-100">
                        {g.items.map((c) => (
                          <li key={c.date + c.shiftId} className="py-0">
                            <button
                              type="button"
                              className="w-full py-2 px-3 flex justify-between gap-3 hover:bg-slate-50 text-left"
                              onClick={() => navigate(`/schedule/${dept.id}?anchor=${c.date}`)}
                              title="Open this day on the schedule"
                            >
                              <span>{c.date}</span>
                              <span className="text-emerald-700 font-semibold whitespace-nowrap">
                                {c.assigned} / {c.minStaffing} staff
                                <span className="ml-1 text-emerald-600">
                                  (+{c.assigned - c.minStaffing})
                                </span>
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AllPendingTimeOffWidget({
  entries, empByIdAll,
}: {
  entries: DeptEntry[];
  empByIdAll: Map<string, Employee>;
}) {
  const totalCount = entries.reduce((sum, e) => sum + e.data.pendingTimeOffList.length, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="font-medium text-slate-900">
          Pending time off
          {totalCount > 0 && (
            <span className="ml-2 text-slate-500 font-normal">({totalCount})</span>
          )}
        </h3>
        {totalCount > 0 && (
          <a
            href="#/time-off"
            className="text-xs font-medium text-sky-700 hover:underline whitespace-nowrap"
          >
            Time Off →
          </a>
        )}
      </div>
      {totalCount === 0 ? (
        <p className="text-sm text-slate-500">No pending time off across departments.</p>
      ) : (
        <div className="space-y-4">
          {entries.map(({ dept, data, deptColor }) => {
            if (data.pendingTimeOffList.length === 0) return null;
            return (
              <div key={dept.id}>
                <DeptSectionHeader dept={dept} deptColor={deptColor} />
                <ul className="text-sm divide-y divide-slate-100">
                  {data.pendingTimeOffList.slice(0, 8).map((t) => {
                    const emp = empByIdAll.get(t.employeeId);
                    const typeCode = TIME_OFF_CODE[t.type];
                    const typeName = TIME_OFF_LABEL[t.type];
                    const sameDay = t.startDate === t.endDate;
                    return (
                      <li key={t.id} className="py-2 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border bg-sky-100 text-sky-800 border-sky-200"
                              title={typeName}
                            >
                              {typeCode}
                            </span>
                            <span className="font-medium text-slate-900 truncate">
                              {emp ? `${emp.lastName}, ${emp.firstName}` : t.employeeId}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {sameDay ? t.startDate : `${t.startDate} → ${t.endDate}`}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {data.pendingTimeOffList.length > 8 && (
                    <li className="pt-2 text-xs text-slate-500 italic">
                      +{data.pendingTimeOffList.length - 8} more
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AllPendingTrainingWidget({
  entries, empByIdAll,
}: {
  entries: DeptEntry[];
  empByIdAll: Map<string, Employee>;
}) {
  const totalCount = entries.reduce((sum, e) => sum + e.data.pendingTrainingList.length, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="font-medium text-slate-900">
          Pending training
          {totalCount > 0 && (
            <span className="ml-2 text-slate-500 font-normal">({totalCount})</span>
          )}
        </h3>
        {totalCount > 0 && (
          <a
            href="#/training"
            className="text-xs font-medium text-sky-700 hover:underline whitespace-nowrap"
          >
            Training →
          </a>
        )}
      </div>
      {totalCount === 0 ? (
        <p className="text-sm text-slate-500">No pending training across departments.</p>
      ) : (
        <div className="space-y-4">
          {entries.map(({ dept, data, deptColor }) => {
            if (data.pendingTrainingList.length === 0) return null;
            return (
              <div key={dept.id}>
                <DeptSectionHeader dept={dept} deptColor={deptColor} />
                <ul className="text-sm divide-y divide-slate-100">
                  {data.pendingTrainingList.slice(0, 8).map((t) => {
                    const emp = empByIdAll.get(t.employeeId);
                    const sameDay = t.startDate === t.endDate;
                    return (
                      <li key={t.id} className="py-2 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900 truncate">
                            {emp ? `${emp.lastName}, ${emp.firstName}` : t.employeeId}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {sameDay ? t.startDate : `${t.startDate} → ${t.endDate}`}
                            {t.status === "l2_approved" && (
                              <span className="ml-2 text-amber-700 font-medium">
                                · L2 approved, awaiting final
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {data.pendingTrainingList.length > 8 && (
                    <li className="pt-2 text-xs text-slate-500 italic">
                      +{data.pendingTrainingList.length - 8} more
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Supervisor Coverage-requests widget. Lists pending coverage
// requests (already dept-filtered by the caller for L2, or
// agency-wide for Owner / L1). Clicking a row opens the review
// modal for that request.
function CoverageRequestsWidget({
  requests, empByIdAll, shiftsById, deptsById, onOpen, deptColor, deptName,
}: {
  requests: CoverageRequest[];
  empByIdAll: Map<string, Employee>;
  shiftsById: Map<string, Shift>;
  deptsById: Map<string, Department>;
  onOpen: (r: CoverageRequest) => void;
  deptColor?: { border: string; dot: string };
  deptName?: string;
}) {
  return (
    <div
      className={
        "bg-white border border-slate-200 rounded-lg p-4"
        + (deptColor ? " border-l-4 " + deptColor.border : "")
      }
    >
      {deptColor && deptName && (
        <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          <span className={"inline-block w-1.5 h-1.5 rounded-full " + deptColor.dot} />
          {deptName}
        </div>
      )}
      <div className="mb-3">
        <h3 className="font-medium text-slate-900">
          Coverage requests
          {requests.length > 0 && (
            <span className="ml-2 text-slate-500 font-normal">({requests.length})</span>
          )}
        </h3>
      </div>
      {requests.length === 0 ? (
        <p className="text-sm text-slate-500">No pending coverage requests.</p>
      ) : (
        <ul className="text-sm divide-y divide-slate-100">
          {requests.slice(0, 8).map((r) => {
            const emp = empByIdAll.get(r.requesterEmployeeId);
            const shift = shiftsById.get(r.shiftId);
            const dept = deptsById.get(r.departmentId);
            return (
              <li key={r.id} className="py-0">
                <button
                  type="button"
                  onClick={() => onOpen(r)}
                  className="w-full py-2 px-2 flex items-start justify-between gap-3 hover:bg-slate-50 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">
                      {emp ? `${emp.lastName}, ${emp.firstName}` : r.requesterEmployeeId}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {shift?.name ?? "shift"}
                      {dept && !deptColor && <span> · {dept.name}</span>}
                      {" · "}{r.date}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {coverageRequestTypeLabel(r.coverageType)}
                    </div>
                  </div>
                  <span className="text-xs font-medium text-sky-700 whitespace-nowrap">
                    Review →
                  </span>
                </button>
              </li>
            );
          })}
          {requests.length > 8 && (
            <li className="pt-2 text-xs text-slate-500 italic">
              +{requests.length - 8} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Per-dept callouts rendered below the flat widget pool: Owner /
// L1 training-awaiting-final banner and the pending-forms strip.
// Only render when there is something to show for this dept.

function DeptCallouts({
  dept: _dept, data, isSupervisor, empByIdAll,
}: {
  dept: Department;
  data: DeptComputed;
  isSupervisor: boolean;
  empByIdAll: Map<string, Employee>;
}) {
  const trainingAwaitingFinal = data.trainingAwaitingFinal;
  const empById = data.empById;
  const pendingByForm = data.pendingByForm;
  const pendingFormsTotal = data.pendingFormsTotal;
  void _dept; // dept reserved for future per-dept context in callouts
  return (
    <>
      {trainingAwaitingFinal.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-sky-900">
              Training awaiting your final approval
              <span className="ml-2 text-sky-600 font-normal">
                ({trainingAwaitingFinal.length})
              </span>
            </h3>
            <a href="#/training" className="text-xs font-medium text-sky-700 hover:underline">
              Open Training →
            </a>
          </div>
          <ul className="divide-y divide-sky-100">
            {trainingAwaitingFinal.slice(0, 5).map((t) => {
              const emp = empById.get(t.employeeId);
              return (
                <li key={t.id} className="py-1.5 flex justify-between gap-3 text-sm">
                  <span className="font-medium text-slate-900 truncate">
                    {emp ? `${emp.lastName}, ${emp.firstName}` : t.employeeId}
                  </span>
                  <span className="text-slate-600 whitespace-nowrap">
                    {t.startDate} → {t.endDate}
                  </span>
                </li>
              );
            })}
            {trainingAwaitingFinal.length > 5 && (
              <li className="pt-1.5 text-xs text-slate-500 italic">
                +{trainingAwaitingFinal.length - 5} more on the Training page
              </li>
            )}
          </ul>
        </div>
      )}
      {pendingByForm.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="font-medium text-slate-900">
              {isSupervisor ? "Pending form submissions" : "My pending forms"}
              <span className="ml-2 text-slate-500 font-normal">
                ({pendingFormsTotal})
              </span>
            </h3>
          </div>
          <div className="space-y-4">
            {pendingByForm.map(({ form, items }) => (
              <div key={form.slug}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                    {form.name}
                    <span className="ml-2 normal-case tracking-normal text-slate-400 font-normal">
                      ({items.length})
                    </span>
                  </div>
                  <a
                    href={`#/forms/${encodeURIComponent(form.slug)}`}
                    className="text-xs font-medium text-sky-700 hover:underline whitespace-nowrap"
                  >
                    Open {form.name} →
                  </a>
                </div>
                <ul className="text-sm divide-y divide-slate-100 border-t border-slate-100">
                  {items.slice(0, 5).map((t) => {
                    const emp = empByIdAll.get(t.employeeId);
                    return (
                      <li
                        key={t.id}
                        className="py-2 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          {isSupervisor && (
                            <div className="font-medium text-slate-900 truncate">
                              {emp ? `${emp.lastName}, ${emp.firstName}` : t.employeeId}
                            </div>
                          )}
                          <div className="text-xs text-slate-500 mt-0.5">
                            Submitted {new Date(t.createdAt).toLocaleDateString()}
                            {t.note && (
                              <span className="ml-2 italic truncate">· {t.note}</span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {items.length > 5 && (
                    <li className="pt-2 text-xs text-slate-500 italic">
                      +{items.length - 5} more
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
